// ================================================================================================
import { Component, Suspense, lazy } from 'react';
import _ from "lodash";
import { TableCellCommonStyle } from '../Widgets/Things/Output/TableStructs';
import { isDefined, isFunction } from '@Utils/Micro';
import { DraggableIconStyle, SortableTableRowStyle } from '@Primitive/Sortable';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import Checkbox from '@Primitive/Checkbox';
import { IconButton } from '@Primitive/Button';
import { red200, grey600 } from '@Utils/Colors';
import Toolbar from '@Primitive/Toolbar';

const Sortable = lazy(() => import("@Primitive/Sortable"));
// ================================================================================================
export interface PageViewWidgetSettingsInfo {
    Key: string;
    Title: string;
    MobileOrder: number;
    IsHidden: boolean;
    MobileIsHidden: boolean;
}
export interface PageViewSettingsProps {
    PageInfo: PageViewWidgetSettingsInfo[];
    onCloseRequest: () => void;
    onSaveRequest: (newInfo: PageViewWidgetSettingsInfo[]) => void;
    toolbarHeight: number;
}
export interface PageViewSettingsState {
    PageInfo: PageViewWidgetSettingsInfo[];
}
// ================================================================================================

export class PageViewSettings extends Component<PageViewSettingsProps, PageViewSettingsState> {
    constructor(props: PageViewSettingsProps) {
        super(props);
        this.state = { PageInfo: _.cloneDeep(this.props.PageInfo) };
    }
    // --------------------------------------------------------------------------------------------
    render() {
        const sortedWidgets = _.sortBy(this.state.PageInfo, x => x.MobileOrder);
        const { toolbarHeight } = this.props;
        // todo: get somehow common style for toolbar (we can use ThemeContext)
        return <div>
            <Toolbar
                title='View Settings'
                style={{
                    zIndex: 1000,
                    position: 'relative',
                    boxShadow: 'rgba(0, 0, 0, 0.16) 0px 3px 10px, rgba(0, 0, 0, 0.23) 0px 3px 10px'
                }}
                rightItems={[
                    <IconButton
                        key='save'
                        tooltip='Save'
                        iconClassName='fas fa-cloud-upload-alt'
                        iconStyle={{ height: toolbarHeight }}
                        onClick={() => {
                            if (isFunction(this.props.onSaveRequest))
                                this.props.onSaveRequest(this.state.PageInfo);
                        }} />,
                    <IconButton
                        key='cancel'
                        tooltip='Cancel'
                        iconClassName='fas fa-times'
                        iconStyle={{
                            color: red200,
                            height: toolbarHeight
                        }}
                        onClick={() => {
                            if (isFunction(this.props.onCloseRequest))
                                this.props.onCloseRequest();
                        }} />
                ]}
            />
            <Suspense fallback={<LazyLoadingAnimation />}>
                <Sortable
                    key='view-settings-table'
                    tableId='view-settings-table'
                    tableHeader={
                        <tr className='settingslist-items-tr-header' >
                            <td colSpan={1} style={TableCellCommonStyle}>
                                <i className='fas fa-align-justify center'
                                    style={{ fontSize: 16, color: grey600 }} />
                            </td>
                            <td colSpan={1} style={TableCellCommonStyle}>
                                <i className='fas fa-desktop center'
                                    style={{ fontSize: 16, color: grey600 }} />
                            </td>
                            <td colSpan={1} style={TableCellCommonStyle}>
                                <i className='fas fa-mobile-alt center'
                                    style={{ fontSize: 16, color: grey600 }} />
                            </td>
                            <td colSpan={2} style={TableCellCommonStyle}>
                                <i className='fas fa-th-large center'
                                    style={{ fontSize: 16, color: grey600 }} />
                            </td>
                        </tr>
                    }
                    items={
                        _.map(sortedWidgets, (v, i: number) => {
                            return (
                                <tr
                                    key={'table-row' + i}
                                    className={(i % 2 == 0) ? "settingslist-items-tr-even" : "settingslist-items-tr-odd"}
                                    style={SortableTableRowStyle}>
                                    <td colSpan={1} style={TableCellCommonStyle}>
                                        <i className='draggable-item fas fa-sort-alt center'
                                            style={{ ...DraggableIconStyle, fontSize: 16, color: grey600 }} />
                                    </td>
                                    <td colSpan={1} style={TableCellCommonStyle}>
                                        <Checkbox
                                            uncheckedIcon='fas fa-eye'
                                            checkedIcon='fas fa-eye-slash'
                                            value={v.IsHidden}
                                            style={{ color: grey600 }}
                                            onClick={() =>
                                                this.setState(s => {
                                                    // Get the related widget.
                                                    let widget = _.find(s.PageInfo, x => x.Key == v.Key);

                                                    // Widget exists?
                                                    if (isDefined(widget)) {
                                                        widget.IsHidden = !v.IsHidden;

                                                        // For hide, update also mobile visibility.
                                                        if (widget.IsHidden)
                                                            widget.MobileIsHidden = true;
                                                    }
                                                    return s;
                                                })} />
                                    </td>
                                    <td colSpan={1} style={TableCellCommonStyle}>
                                        <Checkbox
                                            uncheckedIcon='fas fa-eye'
                                            checkedIcon='fas fa-eye-slash'
                                            value={v.MobileIsHidden}
                                            style={{ color: grey600 }}
                                            onClick={() =>
                                                this.setState(s => {
                                                    // Get the related widget.
                                                    let widget = _.find(s.PageInfo, x => x.Key == v.Key);

                                                    // Widget exists?
                                                    if (isDefined(widget))
                                                        widget.MobileIsHidden = !v.MobileIsHidden;
                                                    return s;
                                                })} />
                                    </td>
                                    <td colSpan={2} style={TableCellCommonStyle}>
                                        {v.Title}
                                    </td>
                                </tr>
                            );
                        })}
                    updateItemsOrder={(oldIndex: number, newIndex: number) => {
                        this.setState(s => {
                            _.map(sortedWidgets, (v: PageViewWidgetSettingsInfo, i: number) => {
                                // Get the related widget.
                                let widgetDesc = _.find(s.PageInfo, x => x.Key == v.Key);

                                // Update its order.
                                if (newIndex > oldIndex) {
                                    if (i < oldIndex || i > newIndex)
                                        widgetDesc.MobileOrder = i;
                                    else if (i == oldIndex)
                                        widgetDesc.MobileOrder = newIndex;
                                    else if (i > oldIndex && i <= newIndex)
                                        widgetDesc.MobileOrder = i - 1;
                                }
                                else if (oldIndex > newIndex) {
                                    if (i > oldIndex || i < newIndex)
                                        widgetDesc.MobileOrder = i;
                                    else if (i == oldIndex)
                                        widgetDesc.MobileOrder = newIndex;
                                    else if (i < oldIndex && i >= newIndex)
                                        widgetDesc.MobileOrder = i + 1;
                                }
                            });
                            return s;
                        });
                    }}
                />
            </Suspense>
        </div>;
    }
}